import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fdate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, StatusBadge, Empty } from "@/components/common";
import { EmployeeDialog } from "@/pages/Employees";
import { Button } from "@/components/ui/button";

export default function EmployeeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [e, setE] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get(`/employees/${id}`).then((r) => setE(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement cette fiche ?")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Fiche supprimée");
    navigate("/employes");
  };

  if (!e) return <p className="text-sm text-slate-500">Chargement…</p>;

  return (
    <div>
      <PageHeader kicker={`Matricule ${e.matricule}`} title={`${e.first_name} ${e.last_name}`}>
        <Button variant="outline" onClick={() => setOpen(true)} data-testid="emp-edit-btn"><Pencil className="mr-1 h-4 w-4" /> Modifier</Button>
        <Button variant="outline" onClick={remove} data-testid="emp-delete-btn"><Trash2 className="h-4 w-4" /></Button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        <Panel title="Fiche" action={<StatusBadge status={e.status} testId="emp-status-badge" />}>
          <dl className="space-y-3 text-sm">
            {[["Poste", e.position], ["Département", e.department], ["Contrat", e.contract_type], ["Embauche", e.hire_date ? fdate(e.hire_date) : "—"], ["Téléphone", e.phone], ["Email", e.email]].map(([k, v]) => (
              <div key={k}><dt className="text-xs uppercase tracking-wider text-slate-500">{k}</dt><dd className="mt-0.5 font-medium">{v || "—"}</dd></div>
            ))}
          </dl>
        </Panel>
        <div className="space-y-6">
          <Panel title="Notes RH (confidentiel)">
            <p className="whitespace-pre-line text-sm text-slate-700" data-testid="emp-notes">{e.notes || "Aucune note."}</p>
          </Panel>
          <Panel title="Historique des congés" action={<Link to="/conges" className="text-xs font-semibold text-red-600 hover:underline">Gérer</Link>}>
            {e.leaves.length ? (
              <ul className="divide-y divide-slate-100 text-sm">
                {e.leaves.map((l) => (
                  <li key={l.id} className="flex items-center justify-between py-2"><span>{l.type} · {fdate(l.start_date)} → {fdate(l.end_date)}</span><StatusBadge status={l.status} /></li>
                ))}
              </ul>
            ) : <Empty text="Aucun congé enregistré." />}
          </Panel>
          <Panel title="Affectations sur interventions">
            {e.interventions.length ? (
              <ul className="divide-y divide-slate-100 text-sm">
                {e.interventions.map((i) => (
                  <li key={i.id} className="flex items-center justify-between py-2">
                    {user.role === "admin" || user.role === "technicien" ? <Link to={`/interventions/${i.id}`} className="hover:text-red-600">{i.number} · {i.title}</Link> : <span>{i.number} · {i.title}</span>}
                    <StatusBadge status={i.status} />
                  </li>
                ))}
              </ul>
            ) : <Empty text="Aucune affectation." />}
          </Panel>
        </div>
      </div>
      <EmployeeDialog open={open} initial={e} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}
